import type { Provider } from './providers';

const CHATGPT_IMPORT_PROMPT = `I'm migrating to a new AI marketing tool for THIS business:
- Website: {{BUSINESS_URL}}
- Instagram: {{BUSINESS_IG_HANDLE}}

Everything in this export should be anchored to that business. I need you to pull
everything you know about me, this business, and the work we've done that's relevant to
it — as a single clean markdown file I can copy in one click. The new tool has NONE of our
history; it's reading this cold, so every entry must carry enough context to make sense and
be acted on without follow-up questions.

Pull from BOTH of your memory sources:
1. Your saved memories about me (the explicit stored list).
2. Everything you can reference from our past chat history.
Work through both thoroughly before writing.

═══════════════════════════════════════════
STEP 0 — ANCHOR TO THE CURRENT BUSINESS (do this first)
═══════════════════════════════════════════
{{BUSINESS_URL}} and {{BUSINESS_IG_HANDLE}} are my CURRENT primary business. Everything you
recall should be judged by its relevance to it. Tag every item:
- [core] — directly about this business: its product, brand, customers, marketing, strategy,
  the work we did ON it.
- [supporting] — about me or my approach in ways that shape how this business should be run
  or marketed (durable preferences, working style, skills, beliefs).
- [legacy] — about OTHER ventures, past businesses, or directions I explored but am not
  currently pursuing.

Scoping rules:
- [core] and [supporting] get full, rich treatment.
- [legacy] gets COMPRESSED HARD. Do not reproduce old businesses' headlines, pricing, copy,
  or campaign fragments verbatim. For each past venture, give 2–3 lines max: what it was and
  what I learned that's relevant to the current business. Nothing more.
- If you're unsure whether something is current, judge by what connects to
  {{BUSINESS_URL}} / {{BUSINESS_IG_HANDLE}}. When still unsure, mark it [legacy] and keep it short.

═══════════════════════════════════════════
STEP 1 — GATHER (silently, before any output)
═══════════════════════════════════════════
Recall everything across these angles, checking saved memories AND chat history, and tag
each by relevance ([core] / [supporting] / [legacy]):
- How I like to communicate and work with you (tone, format, length, structure)
- This business — what it sells, who it serves, stage, location, scale
- My role, goals, and how I make decisions
- Brand, voice, design taste — what I like and what I reject
- Marketing, content, social, SEO, ads, email FOR THIS BUSINESS
- Sales, bizdev, partnerships, pricing
- Hiring, team, operations
- Competitors and market
- Personal facts that shape how I market (background, values, story)
- Recurring constraints and hard "no"s
- DELIVERABLES we produced for this business (see Part 3 — strict bar)

═══════════════════════════════════════════
STEP 2 — SORT
═══════════════════════════════════════════
A) HOW I WORK WITH YOU — durable rules about communication and collaboration. Follow literally.
B) MY BUSINESS — facts, decisions, preferences, context about THIS business.
C) MY WORK & DELIVERABLES — actual reusable artifacts for this business (strict bar, Part 3).
A vs B test: would it still be true if I switched industries? Yes → A. Only matters for THIS
business → B.

═══════════════════════════════════════════
STEP 3 — RECONCILE & DEDUP (critical)
═══════════════════════════════════════════
- Collapse duplicates. State each rule, preference, or fact in EXACTLY ONE place. If a
  carousel rule lives in Format & Structure, it does NOT also appear in Constraints or
  Deliverables. Cross-part repetition is a failure — pick the one canonical home.
- Flag genuine contradictions; judge current vs. stale.
- Compress all [legacy] material as described in Step 0.

═══════════════════════════════════════════
CRITICAL OUTPUT RULES
═══════════════════════════════════════════
- Do all recalling, thinking, and reconciling SILENTLY. Don't narrate it.
- The ENTIRE export must be ONE single markdown code block — nothing else inside it.
- No preamble before the code block. The ONLY thing after it is a single line stating
  whether this is the complete set or if more remains.
- If any entry would contain triple backticks, strip or escape them.

═══════════════════════════════════════════
RICHNESS & PROPORTIONALITY RULES
═══════════════════════════════════════════
- Each entry should read like I'm explaining it to a sharp new teammate who needs to USE it —
  the WHAT and the WHY, actionable without follow-ups. Don't compress into bare one-liners.
  But match depth to what was actually established; never inflate a thin item or invent detail.
- PRESERVE EXACT NUMBERS VERBATIM. Any metric, percentage, count, price, or date stays exactly
  as stated. If a number may be stale, keep it intact and flag it — don't drop or round it.
- [legacy] items are the exception: compress them regardless of how much detail exists.

═══════════════════════════════════════════
OUTPUT — produce exactly this, inside one \`\`\`markdown code block
═══════════════════════════════════════════

# My Memory Export — {{BUSINESS_URL}}

## PART 1 — HOW I WORK WITH YOU
> Durable communication/collaboration rules. Preserve my wording where it's an instruction,
> include the reasoning. Lead each entry with [core]/[supporting].

### Communication & Tone
### Format & Structure
### Working Style

## PART 2 — MY BUSINESS
> First person, in my voice — me introducing THIS business to the new tool. Lead each entry
> with [core]/[supporting]/[legacy]. Keep legacy items to 2–3 lines.

### Who I am and what I'm building
### Brand & voice
### What I've been working on
### Constraints & hard nos
### Still in flux
### Prior ventures & lessons   ← all [legacy] business context goes HERE, compressed

## PART 3 — MY WORK & DELIVERABLES
> The actual reusable WORK we produced FOR THIS BUSINESS. Reproduce in full, verbatim where
> I wrote or approved specific wording. This is the one place a long entry is correct.
>
> STRICT BAR — cap at the 10–15 MOST VALUABLE, MOST REUSABLE assets. Include ONLY:
> foundational docs (ICP/ICA, positioning, mission, value prop, brand/voice guidelines),
> reusable frameworks and skills, finalized core copy/taglines, and reusable image/ad
> prompts I'd want to run again.
> NOT deliverables — do NOT include: one-off design tweaks, icon/UI edit requests,
> single-client or single-post campaign fragments, conversational requests, anything tied to
> a [legacy] venture, or anything I'd never reuse. If your list exceeds 15, you're including
> noise — cut to the most reusable.

For each: 1) WHAT IT IS, 2) THE ACTUAL CONTENT (verbatim, in a sub-block), 3) STATUS
(final/approved/draft/abandoned), 4) RELEVANCE ([core]/[supporting]).
- Image/visual prompts: copy character-for-character, include model/tool/settings if known.
- Partial recall: reproduce what you have, say plainly it's partial — don't fabricate the rest.
- If no real reusable deliverables exist for this business, write "No major deliverables found."

═══════════════════════════════════════════
CONTENT RULES
═══════════════════════════════════════════
- Only state what you actually remember. Never infer or invent. If thin or uncertain, say so.
- Drop off-topic material entirely (coding help, recipes, golf, unrelated hobbies/personal
  questions). Don't even list them as excluded — just omit them.
- Drop any section heading with nothing real behind it.

After the code block, tell me whether this is the complete set or if more remains.`;

const CLAUDE_IMPORT_PROMPT = `I'm migrating to a new AI marketing tool and need to export everything you know about me,
my business, and the work we've done together — as a single clean markdown file I can copy
in one click. The new tool has NONE of our history; it's reading this cold, so every entry
must carry enough context to make sense and be acted on without follow-up questions.

Focus only on memories and context relevant to the following business:
Website: {{website}}
Instagram: {{instagram handle}}

Do all of your searching, thinking, and reconciling SILENTLY. Do not narrate what you're
searching, what you found, or your reasoning. Do not show any work-in-progress, status
updates, or commentary. Your entire visible response is the single markdown code block
described below (rendered inline in chat, not as a file), followed by one closing line.

═══════════════════════════════════════════
STEP 1 — SWEEP (silently, before any output)
═══════════════════════════════════════════
Search your conversation history systematically. Don't stop at the first hits. Run
separate searches across each angle below, one at a time, and keep going until new
searches stop surfacing new material. Reach into the oldest chats too:
- How I like to communicate and work with you (tone, format, length, structure)
- My business — name, what it sells, who it serves, stage, location, scale
- My role, goals, and how I make decisions
- Brand, voice, design taste — what I like and what I reject
- Marketing, content, social, SEO, ads, email
- Sales, bizdev, partnerships, pricing
- Hiring, team, operations
- Competitors and market
- Personal facts that shape how I market (background, values, story)
- Recurring constraints and hard "no"s
- DELIVERABLES we produced: strategy docs, ICP/ICA, business plans, mission statements,
  promotions, ad copy, IG captions, brand guidelines, image-generation prompts, outreach
  drafts, finished or near-finished artifacts of any kind

As you find each item, capture both (1) the full substance — the what, the why, the
specifics — and (2) the date of the conversation it came from, taken from that chat's
timestamp in your search metadata (NOT today's date). If an item recurs, note earliest and
most recent dates.

═══════════════════════════════════════════
STEP 2 — SORT
═══════════════════════════════════════════
A) HOW I WORK WITH YOU — durable rules about communication and collaboration (tone,
   format, length, "always X / never Y," corrections). Instructions to follow literally.
B) MY BUSINESS — context about me and the business: who I am, how I think, what I'm
   focused on and why. Narrative, not artifacts.
C) MY WORK & DELIVERABLES — the actual artifacts we produced, reproduced verbatim.
Borderline A vs B test: would it still be true if I switched industries? Yes → A. Only
matters for THIS business → B.
Borderline B vs C test: am I EXPLAINING something (→ B) or REPRODUCING something (→ C)?
If it's a description of work, it's B. If it's the work itself, it's C.

═══════════════════════════════════════════
STEP 3 — RECONCILE (silently)
═══════════════════════════════════════════
Collapse duplicates, flag contradictions, judge current vs. stale.

═══════════════════════════════════════════
CRITICAL OUTPUT RULES
═══════════════════════════════════════════
- All searching, thinking, and reconciling happens SILENTLY. No narration, no preamble.
- The ENTIRE export is ONE single markdown code block, rendered INLINE in this chat —
  nothing else inside the block.
- Nothing before the code block. The ONLY thing after it is a single line stating whether
  this is the complete set or if more remains.
- If any entry would contain triple backticks, strip or escape them.

═══════════════════════════════════════════
THE THREE PARTS HAVE THREE DIFFERENT VOICES — KEEP THEM DISTINCT
═══════════════════════════════════════════
This is the most important structural rule. Each part is written differently, and they
must not bleed into each other:
- PART 1 = a verbatim, dated LIST of instructions. Stamped lines are correct here.
- PART 2 = first-person NARRATIVE PROSE — me talking to the new tool. NO date-prefix
  stamps; dates live inside the sentences. This is context, never a task log.
- PART 3 = the ACTUAL ARTIFACTS reproduced verbatim. Not described — reproduced.

If you find yourself writing "[date] - Did X. [date] - Did Y" anywhere in Part 2, STOP —
that's ledger voice, and either it belongs in Part 3 (if it's an artifact) or it needs to
be rewritten as narrative (if it's context).

═══════════════════════════════════════════
RICHNESS & PROPORTIONALITY
═══════════════════════════════════════════
- Match each entry's depth to how much was ACTUALLY established. Never inflate a thin item
  or invent detail to hit a richness target. If my history is light in an area, a short or
  empty section is the correct, honest result.
- PRESERVE EXACT NUMBERS VERBATIM. Any metric, percentage, count, price, or date must
  appear exactly as stated — never soften "28% D7 retention, 40% among activated users"
  into "retention was decent." If a number may be stale, keep it intact and flag it.

═══════════════════════════════════════════
DATING RULES
═══════════════════════════════════════════
- Use the conversation's own timestamp from your search metadata. Never today's date. Use
  [unknown] only if there's genuinely no traceable date.
- PART 1: stamp each line with the EARLIEST date (when I first established the rule), as a
  line prefix.
- PART 2: weave dates INTO the prose ("in a late-May 2026 leadership update…"), never as a
  line-leading stamp. Use relative emphasis where it reads better ("I keep returning to
  this" / "this came up once early").
- PART 3: date each deliverable (latest version if revised).
- If dates are sparse or nearly identical (a short history), don't lean on them for
  meaning — lean on relative emphasis instead.

═══════════════════════════════════════════
OUTPUT — produce exactly this, inside one \`\`\`markdown code block, inline in chat
═══════════════════════════════════════════

# My Memory Export

> **How to read this document.** This is a handoff of everything a previous AI assistant
> learned about me and my business, so you can pick up where it left off without making me
> repeat myself. It has three parts, and you should treat each one differently:
> - **Part 1 — How I work with you:** standing rules for how *you* should communicate and
>   collaborate with me. Adopt these as your own operating instructions going forward — they
>   describe how I want to be worked with, not tasks to complete. Follow them literally.
> - **Part 2 — My business:** context about who I am, how I think, and what I'm focused on,
>   in my own voice. Absorb it as background so you understand me — it's not a to-do list.
> - **Part 3 — My work & deliverables:** actual finished artifacts. Reuse these as-is rather
>   than rebuilding them; check the STATUS on each before treating anything as live.
> Dates show how fresh each item is; where things conflict, the most recent wins.

## PART 1 — HOW I WORK WITH YOU
> Standing rules for how you should work with me — adopt these as your own operating
> instructions and follow them literally going forward. They're verbatim where I stated them
> as rules, with the reasoning included so you understand WHY, not just what. This part IS a
> dated list; the stamp prefix is correct here.
Each line: [YYYY-MM-DD] - full entry with substance and reasoning.

### Communication & Tone
### Format & Structure
### Working Style

## PART 2 — MY BUSINESS
> PART 2 IS CONTEXT IN MY VOICE. First-person prose — me introducing myself and my business
> to the new tool so it understands who I am and how I think. It is NOT a list of
> deliverables (those live in Part 3) and NOT a task log. Dates go INSIDE the sentences,
> never as line-prefix stamps.
> Voice check: every entry should sound like something I'd actually SAY out loud. "I've been
> across nearly every layer of this product, and the through-line is…" — yes. "[2026-05-18]
> - Redesigned the request-access form" — no, that's a ticket, not me talking.

### Who I am and what I'm building
### Brand & voice
### What I've been working on
> NARRATIVE, not a task log. Tell the STORY of the work — the themes, the through-lines,
> what I've been pushing on and why — grouped BY THEME, not by date. The artifacts
> themselves go in Part 3; here I give the context that makes them make sense.
### Constraints & hard nos
### Still in flux

## PART 3 — MY WORK & DELIVERABLES
> PART 3 IS THE ARTIFACTS THEMSELVES, reproduced verbatim. It is NOT context or narrative
> (that's Part 2). If you're explaining or summarizing rather than reproducing, it belongs
> in Part 2. Do NOT condense or paraphrase — the value is in the exact wording, and the new
> tool needs to reuse these as-is. This is the one place where a long export is correct.

Pull the FULL artifact (not a description) for anything like: strategy docs; ICP/ICA;
business plan; mission statement; value prop; brand brief; messaging frameworks;
promotions/offers (actual offer, terms, timing, copy); partnership/outreach drafts; IG
captions, hook frameworks, content calendars, tone/brand guidelines, bio copy; ad copy
(headlines, body, CTAs, targeting); image/visual prompts copied character-for-character
including model/tool/settings (Midjourney, Flux, DALL-E); any finished or near-finished
deliverable I'd be annoyed to recreate.

For each deliverable:
1. **WHAT IT IS** — one-line label.
2. **THE ACTUAL CONTENT** — reproduced in full, verbatim. Use blockquotes/sub-blocks so it
   lifts cleanly.
3. **STATUS** — final / approved / draft / abandoned. Mark clearly if unfinished or rejected.
4. **DATE** — when produced (latest version if revised).

Reproduction rules:
- Final/approved deliverables: reproduce IN FULL. Completeness beats brevity.
- Too long to reproduce completely? Reproduce the core verbatim and note what was abridged —
  never silently cut.
- Multiple versions: give the latest/approved one, note earlier versions exist.
- Preserve MY words as authoritative over the AI's where the distinction is clear.
- If you can only partially retrieve something from fragments, reproduce what you can and
  say plainly it's partial — don't reconstruct or fabricate the rest.
- If no real deliverables exist, write "No major deliverables found." Do not upgrade a
  casual chat into a "deliverable."

═══════════════════════════════════════════
CONTENT RULES
═══════════════════════════════════════════
- Only state what you actually found in our chats. Never infer or invent. If something's
  thin or you're unsure how recent it is, say so.
- Drop off-topic material (one-off coding help, recipes, unrelated hobbies). Keep personal
  details only if they plausibly shape marketing.
- Drop any section heading with nothing real behind it.

After the code block, on its own line, tell me whether this is the complete set or if more
remains to export. Nothing else after the block.`;

export { CHATGPT_IMPORT_PROMPT, CLAUDE_IMPORT_PROMPT };

export type PromptVars = {
	websiteUrl: string;
	instagramHandle: string;
};

/**
 * Build a provider's memory-export prompt with the user's website + IG
 * handle interpolated. ChatGPT and Claude use different placeholder tokens.
 */
export const buildImportPrompt = (
	provider: Provider,
	{ websiteUrl, instagramHandle }: PromptVars,
): string => {
	const handle = instagramHandle ? `@${instagramHandle.replace(/^@/, '')}` : '';
	if (provider === 'chatgpt') {
		return CHATGPT_IMPORT_PROMPT.replaceAll(
			'{{BUSINESS_URL}}',
			websiteUrl,
		).replaceAll('{{BUSINESS_IG_HANDLE}}', handle);
	}
	return CLAUDE_IMPORT_PROMPT.replaceAll('{{website}}', websiteUrl).replaceAll(
		'{{instagram handle}}',
		handle,
	);
};
